'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { cn } from '../utils';
import { Button } from './button';
import { SimpleSelect } from './select';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  toYear = new Date().getFullYear() + 1,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      captionLayout="dropdown"
      showOutsideDays={showOutsideDays}
      toYear={toYear}
      className={cn('relative p-3 disabled:pointer-events-none', className)}
      classNames={{
        // Two months sit side by side from 640px.
        months: 'flex flex-col gap-4 sm:flex-row',
        month: 'w-full space-y-4',
        // A plain caption ("September 2026") shares one row with the prev/next arrows; the
        // dropdown caption keeps its two rows with the arrows beside the month.
        month_caption:
          props.captionLayout === 'label'
            ? 'flex h-9 touch:h-10 items-center justify-center'
            : 'flex gap-2',
        caption_label: 'text-sm font-medium text-center',
        nav:
          props.captionLayout === 'label'
            ? 'absolute top-3 left-3 right-3 flex h-9 touch:h-10 items-center justify-between'
            : 'absolute top-12 flex items-center justify-between left-3 right-3',
        button_previous:
          'h-8 w-8 touch:h-10 touch:w-10 flex items-center justify-center bg-transparent p-0 hover:bg-accent hover:text-accent-foreground disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed rounded-md border border-input',
        button_next:
          'h-8 w-8 touch:h-10 touch:w-10 flex items-center justify-center bg-transparent p-0 hover:bg-accent hover:text-accent-foreground disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed rounded-md border border-input',
        month_grid: 'w-full space-y-1 border-collapse',
        // The weekday header and the day cells are both seven-column grids, but a day cell is a
        // fixed 36px while a header cell fills its track. A fixed-width grid item defaults to the
        // start of its track, so the two rows only lined up where the grid happened to be
        // 7 x 36px: in a popover the days sat 1.4px left of their header, and in a wide inline
        // container (the window limits dialog) ~25px. Centring the day in its track lines them up
        // at any width.
        weekdays: cn('mt-4 grid grid-cols-7', props.showWeekNumber && 'grid-cols-8'),
        weekday: 'text-sm font-medium text-center text-muted-foreground',
        week: 'mt-2 grid grid-cols-7',
        day: 'relative h-9 w-9 touch:h-10 touch:w-10 justify-self-center p-0 font-normal rounded-md hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:opacity-100 data-[selected]:bg-primary data-[selected]:text-primary-foreground disabled:opacity-50 hover:bg-transparent',
        day_button: 'h-9 w-9 touch:h-10 touch:w-10 p-0 font-normal',
        day_selected:
          '!bg-primary !text-primary-foreground hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground',
        // Today reads bold; no border (a dotted ring read as a second selection).
        // Bold only in its own month: a day shown from the next or previous month is never
        // bold, even when it's today. The attribute selector outranks font-normal.
        today: '[&:not([data-outside])]:font-bold [&:not([data-outside])>button]:font-bold',
        outside: 'text-muted-foreground/50',
        disabled: 'text-muted-foreground/50 hover:bg-transparent',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none',
        range_end: 'rounded-l-none',
        range_start: 'rounded-r-none',
        dropdowns: 'flex justify-between px-10 flex-col-reverse gap-2 flex-1',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        PreviousMonthButton: ({ ...props }) => (
          <Button {...props} variant="outline" size="icon">
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
        ),
        NextMonthButton: ({ ...props }) => (
          <Button {...props} variant="outline" size="icon">
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        ),
        Dropdown: (props) => {
          const { options, value } = props;
          const _options =
            options?.map((option) => ({
              value: option.value.toString(),
              label: option.label,
            })) || [];
          return (
            <SimpleSelect
              triggerClassName="z-50 flex-1"
              values={_options}
              selectedValue={value?.toString() || ''}
              onChange={(newValue) => {
                // @ts-expect-error - requires native event, passing only necessary field instead
                props.onChange({ target: { value: newValue } });
              }}
            />
          );
        },
      }}
      {...props}
    />
  );
};
Calendar.displayName = 'Calendar';

export { Calendar };
